/**
 * @file: attachmentActions.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Platform-aware pickers for the chat attachment panel. Image/video use
 * expo-image-picker (multi-select, cap 100). Camera capture uses the same
 * library's camera launcher. Arbitrary documents use a DOM file input on
 * web (native needs expo-document-picker, not yet a dependency).
 */

import { ChatMediaUploadAsset } from '@euler/app/flows/chat/utils/uploadChatMedia';
import * as ImagePicker from 'expo-image-picker';
import { Platform } from 'react-native';

/** WeChat-style cap on a single image/video selection. */
export const MAX_CHAT_MEDIA = 100;

function assetToUpload(a: ImagePicker.ImagePickerAsset): ChatMediaUploadAsset {
  // `mimeType` isn't in this SDK's ImagePickerAsset typings but is present at
  // runtime (esp. on web). On web `a.type` is often undefined for videos, so
  // detect the kind from `a.type` OR the mime type — relying on `a.type` alone
  // mislabels a picked video as an image.
  const mimeType = (a as { mimeType?: string }).mimeType;
  const kind =
    a.type === 'video' || (mimeType ?? '').startsWith('video/')
      ? 'video'
      : 'image';
  return {
    kind,
    uri: a.uri,
    mimeType: mimeType ?? (kind === 'video' ? 'video/mp4' : 'image/jpeg'),
  };
}

/** Pick one or more images/videos from the library (cap {@link MAX_CHAT_MEDIA}). */
export async function pickChatMedia(): Promise<ChatMediaUploadAsset[]> {
  if (Platform.OS === 'android') {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') throw new Error('需要相册访问权限');
  }
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.All,
    allowsMultipleSelection: true,
    selectionLimit: MAX_CHAT_MEDIA,
    quality: 0.85,
  });
  if (result.canceled) return [];
  return result.assets.slice(0, MAX_CHAT_MEDIA).map(assetToUpload);
}

/** Capture a single photo/video with the camera. */
export async function captureChatMedia(): Promise<ChatMediaUploadAsset[]> {
  const { status } = await ImagePicker.requestCameraPermissionsAsync();
  if (status !== 'granted') throw new Error('需要相机访问权限');
  const result = await ImagePicker.launchCameraAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.All,
    quality: 0.85,
  });
  if (result.canceled) return [];
  return result.assets.map(assetToUpload);
}

export type PickedFile = {
  uri: string;
  filename: string;
  mimeType: string;
  sizeInBytes: number;
};

/** Pick a document. Web-only for now (native needs expo-document-picker). */
export async function pickChatFile(): Promise<PickedFile | null> {
  if (Platform.OS !== 'web') {
    throw new Error('当前仅支持在网页端发送文件');
  }
  return await new Promise<PickedFile | null>(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept =
      '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,application/*';
    // Some browsers ignore `.click()` on a detached input, so mount it
    // hidden and clean up once a file is chosen.
    input.style.display = 'none';
    document.body.appendChild(input);
    const cleanup = () => {
      input.remove();
    };
    // A closed picker sends `cancel`, not `change`: without it the promise
    // would never settle.
    input.oncancel = () => {
      cleanup();
      resolve(null);
    };
    input.onchange = () => {
      const file = input.files?.[0];
      cleanup();
      if (!file) {
        resolve(null);
        return;
      }
      resolve({
        uri: URL.createObjectURL(file),
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeInBytes: file.size,
      });
    };
    input.click();
  });
}
